import React from 'react';
import { Brain, User, Sun, Moon, SunMoon, KeyRound, Sparkles } from 'lucide-react';
import { useTheme } from '../../../new/context/ThemeContext';
import { ApiTokenModal, useApiTokenSettingsOpener } from './ApiTokenModal';
import { MODE_HINT_KEY } from '../../../auth/AuthGate';
import { AccountMenuSlot } from '../../../auth/AccountMenuContext';

/** W trybie logowania (session) sesja zastępuje klucz Bearer — przycisk klucza jest wtedy zbędny. */
function sessionModeActive(): boolean {
  try { return localStorage.getItem(MODE_HINT_KEY) === 'session'; } catch { return false; }
}

interface HeaderProps {
  onLoadTestData: () => void;
}

/** Cykl przełączania motywu zgodny z uiStore.toggleTheme: system → light → dark → system */
const THEME_LABELS: Record<'light' | 'dark' | 'system', string> = {
  system: 'systemowy',
  light: 'jasny',
  dark: 'ciemny',
};

const NEXT_THEME: Record<'light' | 'dark' | 'system', 'light' | 'dark' | 'system'> = {
  system: 'light',
  light: 'dark',
  dark: 'system',
};

// Dane testowe to narzędzie deweloperskie — na produkcji nie konkuruje o uwagę z pracą diagnosty.
const IS_DEV_HOST = typeof window !== 'undefined'
  && /^(wisc-v\.ruypex\.pl|localhost|127\.0\.0\.1)$/.test(window.location.hostname);

export const Header: React.FC<HeaderProps> = ({ onLoadTestData }) => {
  const { theme, toggleTheme } = useTheme();
  const [tokenOpen, setTokenOpen] = useApiTokenSettingsOpener();

  const ThemeIcon = theme === 'system' ? SunMoon : theme === 'dark' ? Moon : Sun;
  const ariaLabel = `Motyw: ${THEME_LABELS[theme]}. Przełącz na ${THEME_LABELS[NEXT_THEME[theme]]}`;

  // Wspólny wygląd przycisków-ikon w pasku (min. 40 px — wygodne na dotyk). Etykiety tekstowe
  // dopiero od xl: przy 1024 px z plakietką konta pasek łamałby się na 2 wiersze (sticky + wysokość kolumn).
  const iconBtn =
    'h-10 min-w-10 px-2.5 inline-flex items-center justify-center gap-2 rounded-lg border border-brd-strong bg-surface-card text-txt-secondary hover:text-txt-primary hover:border-accent-border text-xs font-semibold whitespace-nowrap transition-colors duration-200 cursor-pointer';

  return (
    // glass-bar: pasek szkła z blur; sticky od md (na telefonie nagłówek przewija się z treścią,
    // żeby nie zajmował ćwierci ekranu). Bez overflow-hidden — menu konta wychodzi poza pasek.
    <header className="app-header glass-bar md:sticky md:top-3 z-header rounded-2xl px-4 py-3 sm:px-5 md:py-3.5 text-txt-primary flex flex-wrap items-center justify-between gap-x-4 gap-y-3">
      <div className="flex items-center gap-3 min-w-0">
        <div className="p-2.5 rounded-xl bg-accent-soft border border-accent-border shrink-0">
          <Brain className="w-6 h-6 sm:w-7 sm:h-7 text-accent-strong" />
        </div>
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-extrabold tracking-tight text-txt-primary leading-tight">
            {/* nowrap: „WISC-V” nie łamie się na myślniku („WISC-” / „V”) na wąskich ekranach */}
            Diagnostyka <span className="whitespace-nowrap">WISC-V</span>
          </h1>
          <p className="text-xs text-txt-secondary mt-0.5">
            Wsparcie interpretacji wyników · ostateczną ocenę zawsze formułuje psycholog
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2 ml-auto">
        <a
          href="/new"
          className={iconBtn}
          aria-label="Nowa wersja interfejsu (beta)"
          title="Nowa wersja interfejsu (beta)"
        >
          <Sparkles className="w-4 h-4" />
          <span className="hidden xl:inline">Nowa wersja</span>
          <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-accent-soft text-accent-strong">beta</span>
        </a>

        {IS_DEV_HOST && (
          <button
            type="button"
            onClick={onLoadTestData}
            className={`${iconBtn} border-dashed bg-transparent`}
            aria-label="Dane testowe"
            title="Tylko środowisko DEV"
          >
            <User className="w-4 h-4" />
            <span className="hidden xl:inline">Dane testowe</span>
          </button>
        )}

        {!sessionModeActive() && (
          <button
            type="button"
            onClick={() => setTokenOpen(true)}
            aria-label="Klucz dostępu do zapisanych diagnoz"
            title="Klucz dostępu do zapisanych diagnoz"
            className={iconBtn}
          >
            <KeyRound className="w-4 h-4" />
          </button>
        )}

        <button
          type="button"
          onClick={toggleTheme}
          aria-label={ariaLabel}
          title={ariaLabel}
          className={iconBtn}
        >
          <ThemeIcon className="w-4 h-4" />
        </button>

        {/* Menu konta w przepływie paska (tylko przy logowaniu) — nie nachodzi na nagłówek ani karty */}
        <AccountMenuSlot />
      </div>
      {tokenOpen && <ApiTokenModal onClose={() => setTokenOpen(false)} />}
    </header>
  );
};
